/**
 * One clipboard ladder for every "copy this" affordance.
 *
 * Four separate implementations grew four separate bugs, and the lobby's was
 * the reported one ("copy link button seems to not work on ios"): its
 * fallback selected a READONLY textarea parked at top:-1000px — iOS Safari
 * refuses .select() on a readonly control and refuses to copy from a node
 * outside the viewport — and its last resort was window.prompt, which iOS
 * suppresses outside a gesture. Every rung either works on iOS or truthfully
 * reports failure so the caller can show one.
 *
 * Call synchronously from the user's gesture — the modern API's permission is
 * tied to transient activation.
 *
 * Every rung must also END. iOS Brave, 2026-09-15: "Copy link" pressed after
 * the share sheet did nothing the player could see — no toast, no label
 * change, a button that read as a mock-up until the browser was restarted.
 * An awaited clipboard promise that never settles is exactly that: the caller
 * sits at `await` forever and its feedback never runs. So the modern rung is
 * given a bound and abandoned past it, and the ladder always comes back with
 * a verdict the caller can show.
 */
let CopyText = {};

CopyText = (function () {
    /* How long the modern API gets before the ladder moves on. Every engine
       that implements writeText settles it in milliseconds (WebKit writes the
       pasteboard synchronously and resolves at once); this is a ceiling for a
       browser that has stopped answering, not a budget the happy path uses.

       UNDER ONE SECOND ON PURPOSE. WebKit forwards the user gesture into a
       setTimeout callback only when the timer was armed inside the gesture
       with a delay of at most 1s (DOMTimer's gesture forwarding), and the
       legacy rung is execCommand('copy'), which WebKit allows only while a
       gesture is being processed. Measured in Playwright WebKit: a 1500ms
       bound left the legacy rung refused ("Could not copy the link"); 900ms
       let it copy. Chromium and Firefox grant execCommand on transient
       activation (~5s) either way. */
    const CLIPBOARD_SETTLE_MS = 900;

    /**
     * Race a clipboard promise against the bound.
     * @returns {Promise<boolean>} true = it resolved; false = the bound
     *   elapsed first. A rejection propagates so the caller treats it as a
     *   refused rung, the same as before.
     */
    function settledWithin(promise, ms) {
        let timer;
        const expiry = new Promise(resolve => {
            timer = setTimeout(() => resolve(false), ms);
            timer.unref?.();
        });
        return Promise.race([
            Promise.resolve(promise).then(() => true),
            expiry,
        ]).finally(() => clearTimeout(timer));
    }

    function legacySelectionCopy(text) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        /* iOS-compatible selection: writable (never readonly), inside the
           viewport, and at 16px so focusing it cannot zoom the page. It stays
           invisible via opacity, not position. */
        textarea.contentEditable = 'true';
        textarea.style.position = 'fixed';
        textarea.style.left = '0';
        textarea.style.top = '0';
        textarea.style.width = '10px';
        textarea.style.height = '24px';
        textarea.style.opacity = '0';
        textarea.style.fontSize = '16px';
        textarea.setAttribute('aria-hidden', 'true');

        document.body.appendChild(textarea);
        const selection = document.getSelection();
        const existingRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        let copied = false;
        try {
            textarea.focus();
            textarea.setSelectionRange(0, text.length);
            copied = document.execCommand('copy') === true;
        } catch (_) {
            copied = false;
        } finally {
            textarea.remove();
            if (selection) {
                selection.removeAllRanges();
                if (existingRange) selection.addRange(existingRange);
            }
        }
        return copied;
    }

    /**
     * @param {string} text what to put on the clipboard
     * @param {{ shareTitle?: string, allowShareFallback?: boolean }} [options]
     *   sheet dressing, and whether the sheet rung is allowed at all — a
     *   caller that renders its own Share button next to Copy passes false so
     *   a failed copy cannot surprise-open the sheet.
     * @returns {Promise<{ok: boolean, method: 'clipboard'|'legacy'|'share'|null}>}
     *   method 'share' means the SYSTEM SHEET was shown (it carries its own
     *   Copy action) — the caller should not claim "copied" for it.
     */
    async function copy(text, { shareTitle, allowShareFallback = true } = {}) {
        const value = String(text ?? '');
        if (!value) return { ok: false, method: null };

        /* Presence IS the security gate: browsers only expose
           navigator.clipboard in secure contexts, and an explicit
           isSecureContext check reads undefined in test environments. */
        if (navigator.clipboard?.writeText) {
            try {
                if (await settledWithin(navigator.clipboard.writeText(value), CLIPBOARD_SETTLE_MS)) {
                    return { ok: true, method: 'clipboard' };
                }
                /* Never answered: fall through to a rung that answers now. */
            } catch (_) { /* permissions policy, no activation, or iOS refusal */ }
        }

        try {
            if (legacySelectionCopy(value)) return { ok: true, method: 'legacy' };
        } catch (_) { /* fall through */ }

        /* The sheet is the last rung, not a preference: tapping "Copy" should
           copy, not open UI. But where both clipboard paths are refused
           (embedded webviews, older iOS), the sheet is the one surface the
           platform always allows — and it contains Copy. */
        if (allowShareFallback && typeof navigator.share === 'function') {
            const payload = /^https?:\/\//i.test(value)
                ? { url: value, ...(shareTitle ? { title: shareTitle } : {}) }
                : { text: value, ...(shareTitle ? { title: shareTitle } : {}) };
            try {
                await navigator.share(payload);
                return { ok: true, method: 'share' };
            } catch (_) { /* dismissed or unavailable */ }
        }

        return { ok: false, method: null };
    }

    return { copy };
})();

export { CopyText };
